import { FC } from 'react';
import Box, { Flex } from '@spotnana/pixel-react/dist/Box';
import { useTranslation } from 'react-i18next';
import { V2TripDetailsResponseManager, dateFormats, dateUtils, localizeDate } from 'obt-common';
import DotSeparator from '../../../../v2-components/shared/DotSeparator';
import CopyableText from '../../../../v2-components/shared/CopyableText';

import {
  Wrapper,
  DetailsLabel,
  DetailsValue,
  DetailsHeading,
  DetailsGroup,
  TripName,
  TripIdContainer,
} from './TripDetails.style';
import useTripDetails from '../../../../hooks/useTripDetails';

const { dateUtil } = dateUtils;

type TripDetailsProps = {
  tripId: string;
  className?: string;
  showTitle?: boolean;
};

const TripDetails: FC<TripDetailsProps> = ({ tripId, className, showTitle = true }) => {
  const { t: tt } = useTranslation('WEB');

  const { data: tripDetailsResponse, isLoading, isError } = useTripDetails(tripId);

  if (isLoading || isError || !tripDetailsResponse) {
    return null;
  }

  // eslint-disable-next-line no-restricted-syntax
  const tripDetailsResponseManager = new V2TripDetailsResponseManager(tripDetailsResponse);
  const tripInfo = tripDetailsResponseManager?.GetTripInfo();

  const name = tripDetailsResponseManager.GetTripName();
  const { start, end } = tripInfo;

  return (
    <Wrapper className={className}>
      {showTitle && <DetailsHeading>{tt('Trip Details')}</DetailsHeading>}

      <TripName>{name}</TripName>

      <DetailsGroup>
        <Flex>
          <DetailsLabel>{tt('Starting')}:</DetailsLabel>
          <DetailsValue>{start && localizeDate(dateUtil(start, dateFormats.ISO_SHORT), 'medium')}</DetailsValue>
        </Flex>

        <Box>
          <DotSeparator gap={8} />
        </Box>

        <Flex>
          <DetailsLabel>{tt('Ending')}:</DetailsLabel>
          <DetailsValue>{end && localizeDate(dateUtil(end, dateFormats.ISO_SHORT), 'medium')}</DetailsValue>
        </Flex>
        <Box>
          <DotSeparator gap={8} />
        </Box>
        <TripIdContainer>
          <DetailsLabel>{tt('Trip ID')}:</DetailsLabel>
          <CopyableText
            text={tripId}
            aria-label={tt('Copy Trip ID, {{tripId}}', {
              tripId,
            })}
          />
        </TripIdContainer>
      </DetailsGroup>
    </Wrapper>
  );
};

export default TripDetails;
